import React, {
	useMemo,
	useState,
	useContext,
	useCallback,
	createContext,
	PropsWithChildren,
} from 'react'

import { LoanRole } from '@matech/thebigpos-sdk'

import { UserRole } from '@/types'
import { LoanUserRow } from '@/components/loanAppDetails/loan-users-table/types'
import {
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_NUMBER,
} from '../../../../constants/pagination'
import useShareLoan from '../../../../hooks/useShareLoan'
import ModalShareLoan from '../../../../components/modals/ModalShareLoan'

export type LoanType = 'complete' | 'draft'

type LocalLoanShareState = {
	isModalVisible: boolean
	loanId?: string
	loanType?: LoanType
	sharedUsers?: UserRole[]
}

export type HandleOpenShareLoanModalParams = {
	loanId?: string | null
	loanType: LoanType
}

export type PipelineState = {
	handleOpenShareLoanModal?: (
		params: HandleOpenShareLoanModalParams
	) => void
}

const PipelineContext = createContext<PipelineState | undefined>(
	undefined
)

const INITIAL_STATE_LOCAL_SHARE_LOAN: LocalLoanShareState = {
	isModalVisible: false,
	loanId: undefined,
	loanType: undefined,
}

const PipelineProvider = ({ children }: PropsWithChildren) => {
	const [localShareLoanState, setLocalShareLoanState] =
		useState<LocalLoanShareState>(INITIAL_STATE_LOCAL_SHARE_LOAN)

	// ToDo: Need to use this until backend implement a update route
	// If use the loading pending from hook useShareLoan, the add loading will
	// be triggered in update too
	const [isLoadingAdding, setIsLoadingAdding] = useState(false)

	const { loanId, isModalVisible } = localShareLoanState
	const isDraft = localShareLoanState.loanType === 'draft'

	const {
		sharedUsersQuery,
		updateSharedUserRole,
		addSharedUserMutation,
		revokeSharedUserMutation,
	} = useShareLoan({
		loanId,
		isDraft,
		paginationParams: {
			pageNumber: DEFAULT_PAGE_NUMBER,
			pageSize: DEFAULT_PAGE_SIZE,
		},
	})

	const handleOpenShareLoanModal = useCallback(
		({ loanId, loanType }: HandleOpenShareLoanModalParams) => {
			if (!loanId) return

			setLocalShareLoanState({
				loanId,
				loanType,
				isModalVisible: true,
			})
		},
		[]
	)

	const handleCloseShareLoanModal = () => {
		setLocalShareLoanState(INITIAL_STATE_LOCAL_SHARE_LOAN)
	}

	const handleAddUser = (user: LoanUserRow, role: LoanRole) => {
		setIsLoadingAdding(true)
		addSharedUserMutation
			.mutateAsync({
				userId: user.id,
				loanRole: role,
				updateCache: true,
			})
			.finally(() => setIsLoadingAdding(false))
	}

	const handleRevokeUser = (userId: string) =>
		revokeSharedUserMutation.mutateAsync({
			userId,
			updateCache: true,
		})

	const handleChangeRoleItem = (userId: string, role: LoanRole) =>
		updateSharedUserRole({
			userId,
			role,
			updateCache: true,
		})

	const contextValue = useMemo(
		() => ({
			handleOpenShareLoanModal,
		}),
		[handleOpenShareLoanModal]
	)

	return (
		<PipelineContext.Provider value={contextValue}>
			{children}

			<ModalShareLoan
				visible={isModalVisible}
				isDraft={isDraft}
				onClose={handleCloseShareLoanModal}
				showActions={false}
				addUser={{
					onAdd: handleAddUser,
					isLoading: isLoadingAdding,
				}}
				revokeUser={{
					onRevoke: handleRevokeUser,
					isLoading: revokeSharedUserMutation.isPending,
				}}
				roleChange={{
					onChange: handleChangeRoleItem,
				}}
				isLoading={sharedUsersQuery.isLoading}
				initialSelectedUsersRoles={sharedUsersQuery.data}
			/>
		</PipelineContext.Provider>
	)
}

const usePipelineShareContext = () => {
	const context = useContext(PipelineContext)

	if (!context) {
		throw new Error(
			'usePipelineShareContext must be used within a PipelineProvider'
		)
	}

	return context
}

export { usePipelineShareContext }
export default PipelineProvider
